// Tiny fetch wrapper. Throws ApiError on non-2xx so handlers can catch a single type.

import type {
  OnboardingStepKey,
  GlobalOnboardingStepsState,
  CourseOnboardingStepsState,
  CourseOnboardingByCourseMap,
  AdminUserView,
  CreateAdminUser,
  UpdateAdminRole,
} from '@referrals/shared';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export class ApiTimeoutError extends Error {
  constructor(public readonly ms: number) {
    super(
      `La solicitud tardó más de ${Math.round(ms / 1000)} segundos y fue cancelada. Reintentá.`,
    );
    this.name = 'ApiTimeoutError';
  }
}

type ApiOptions = Omit<RequestInit, 'body' | 'signal'> & {
  body?: unknown;
  /** Send cookies cross-origin. Default: 'include' so Vite proxy works. */
  withCredentials?: boolean;
  /** Timeout in ms (default 30s). Pass 0 to disable. */
  timeoutMs?: number;
  /** External AbortSignal to cancel the request. */
  signal?: AbortSignal;
};

export async function api<T = unknown>(path: string, options: ApiOptions = {}): Promise<T> {
  const {
    body,
    headers,
    withCredentials = true,
    timeoutMs = 30000,
    signal: externalSignal,
    ...rest
  } = options;

  // Compose an AbortController that fires on timeout OR external cancel
  const controller = new AbortController();
  let timedOut = false;
  const onExternalAbort = () => controller.abort();
  if (externalSignal) {
    if (externalSignal.aborted) controller.abort();
    else externalSignal.addEventListener('abort', onExternalAbort);
  }
  const timer =
    timeoutMs > 0
      ? setTimeout(() => {
          timedOut = true;
          controller.abort();
        }, timeoutMs)
      : null;

  try {
    // FormData carries its own multipart boundary; letting fetch set
    // Content-Type automatically is what multer needs. Forcing JSON
    // here broke every upload (adminUploadMaterial, uploadDocument).
    const isFormData = body instanceof FormData;
    let res: Response;
    try {
      res = await fetch(path, {
        ...rest,
        credentials: withCredentials ? 'include' : 'same-origin',
        signal: controller.signal,
        headers: isFormData
          ? { Accept: 'application/json', ...headers }
          : { 'Content-Type': 'application/json', Accept: 'application/json', ...headers },
        body: isFormData ? body : body !== undefined ? JSON.stringify(body) : undefined,
      });
    } catch (fetchErr) {
      // fetch rejected before getting a response: timeout, network
      // failure, or external signal. Normalize to a typed error with a
      // Spanish message so the UI doesn't leak browser-level strings
      // like "signal is aborted without reason".
      if (timedOut) {
        throw new ApiTimeoutError(timeoutMs);
      }
      if (externalSignal?.aborted) {
        // Caller-provided abort: re-throw as-is (caller knows it was
        // intentional, e.g. unmount cleanup).
        throw fetchErr;
      }
      if (fetchErr instanceof DOMException && fetchErr.name === 'AbortError') {
        throw new ApiError(
          0,
          'aborted',
          'La solicitud fue cancelada antes de completarse. Reintentá.',
        );
      }
      throw new ApiError(
        0,
        'network',
        'No pudimos conectar con el servidor. Revisá tu conexión e intentá de nuevo.',
      );
    }

    if (timedOut) {
      throw new ApiTimeoutError(timeoutMs);
    }

    const contentType = res.headers.get('content-type') ?? '';
    const isJson = contentType.includes('application/json');
    const data = isJson ? await res.json() : await res.text();

    if (!res.ok) {
      const error = (
        data as { error?: { code: string; message: string; details?: Record<string, unknown> } }
      )?.error;
      throw new ApiError(
        res.status,
        error?.code ?? 'unknown',
        error?.message ?? `Request failed with status ${res.status}`,
        error?.details,
      );
    }

    return data as T;
  } finally {
    if (timer) clearTimeout(timer);
    if (externalSignal) externalSignal.removeEventListener('abort', onExternalAbort);
  }
}

// ---- Typed API helpers used by the panels ----

export interface Course {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  defaultCommissionPct: string;
  defaultCookieTtlHours: number;
  /**
   * D1a: precio del curso en centavos. 0 = "precio a definir".
   */
  priceCents: number;
  releasedAt: string | null;
  status: 'draft' | 'released' | 'archived';
  metadata: string;
  createdAt: string;
  updatedAt: string;
}

export interface AffiliateCourse {
  id: string;
  affiliateId: string;
  affiliateName?: string;
  affiliateCode?: string;
  affiliateEmail?: string;
  courseId: string;
  courseSlug?: string;
  courseName?: string;
  /** D1a: precio del curso en centavos (0 = "precio a definir"). */
  coursePriceCents?: number;
  /** D3a: schedule futuro (e.g. [{pct:50, startsAt, endsAt}, ...]). Hoy siempre undefined. */
  commissionSchedule?: Array<{ pct: number; startsAt: string | null; endsAt: string | null }>;
  code: string;
  commissionPct: number;
  cookieTtlHours: number;
  status: 'pending' | 'active' | 'suspended';
  linkGeneratedAt: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AdminAffiliate {
  id: string;
  code: string;
  email: string;
  name: string;
  country: string | null;
  /** Per-curso RUT/ID del creator (p.ej. DNI, CP, etc). Distinto del RUT
   * bancario que vive dentro de `bankInfo`. */
  rut: string | null;
  phone: string | null;
  instagram: string | null;
  youtube: string | null;
  tiktok: string | null;
  facebook: string | null;
  linkedin: string | null;
  /** Tamaño de audiencia predefinido (<1k, 1k-5k, etc). */
  audienceSize: string | null;
  status: 'pending' | 'active' | 'suspended' | 'rejected';
  commissionPct: string;
  acceptedTosAt: string | null;
  onboardingSteps: GlobalOnboardingStepsState;
  linkGeneratedAt: string | null;
  createdAt: string;
}

export interface CourseAssignmentSummary {
  courseId: string;
  courseName: string | null;
  courseSlug: string | null;
  status: 'pending' | 'active' | 'suspended';
  linkGeneratedAt: string | null;
  onboardingSteps: CourseOnboardingStepsState;
}

export interface AffiliateOnboarding {
  id: string;
  email: string;
  name: string;
  code: string;
  status: 'pending' | 'active' | 'suspended' | 'rejected';
  onboardingSteps: GlobalOnboardingStepsState;
  linkGeneratedAt: string | null;
  courseOnboarding: CourseOnboardingByCourseMap;
  assignments: CourseAssignmentSummary[];
}

export interface AdminCreateAffiliateInput {
  email: string;
  name: string;
  commissionPct: number;
  code?: string;
  country?: string;
  rut?: string;
  phone?: string;
  instagram?: string;
  youtube?: string;
  tiktok?: string;
  facebook?: string;
  linkedin?: string;
  audienceSize?: string;
}

/**
 * Body de PATCH /api/v1/admin/affiliates/:id. Cubre tanto el inline-edit
 * de la ficha del creator como la edición puntual de status/comisión.
 * Cada campo es opcional — el caller envía sólo los que cambió.
 */
export interface UpdateAffiliateInput {
  name?: string;
  status?: 'pending' | 'active' | 'suspended' | 'rejected';
  commissionPct?: number;
  country?: string;
  rut?: string;
  phone?: string;
  instagram?: string;
  youtube?: string;
  tiktok?: string;
  facebook?: string;
  linkedin?: string;
  audienceSize?: string;
  bankInfo?: unknown;
  metadata?: Record<string, unknown>;
}

export interface AdminAffiliateListResponse {
  items: AdminAffiliate[];
  total: number;
  page: number;
  limit: number;
}

export interface AffiliateStats {
  clicks: number;
  leads: number;
  conversions: number;
  totalRevenueCents: number;
  totalCommissionCents: number;
  approvedCommissionCents: number;
  pendingCommissionCents: number;
  paidCommissionCents: number;
}

export interface AdminConversion {
  id: string;
  affiliateId: string | null;
  affiliateName?: string | null;
  affiliateEmail?: string | null;
  affiliateCode?: string | null;
  affiliateCountry?: string | null;
  leadId: string | null;
  externalOrderId: string;
  externalSource: 'lw' | 'shopify';
  amountCents: number;
  currency: string;
  commissionCents: number;
  commissionPct: string;
  status: 'pending' | 'approved' | 'paid' | 'rejected';
  customerEmail: string;
  metadata: string;
  attributedAt: string;
  approvedAt: string | null;
  paidAt: string | null;
  createdAt: string;
}

export interface ConversionListResponse {
  items: AdminConversion[];
  total: number;
  page: number;
  limit: number;
  products?: string[];
}

export interface AffiliateProductStats {
  product: string;
  clicks: number;
  conversions: number;
  conversionRate: number;
  totalCommissionCents: number;
  pendingCommissionCents: number;
  paidCommissionCents: number;
}

export interface AffiliateProductsResponse {
  items: AffiliateProductStats[];
  range: { from: string; to: string };
}

export interface AffiliateDocument {
  id: string;
  affiliateId: string;
  affiliateName: string;
  affiliateEmail: string;
  periodMonth: number;
  periodYear: number;
  fileName: string;
  mimeType: string;
  fileSize: number;
  uploadedAt: string;
}

export interface AffiliateDocumentsResponse {
  items: AffiliateDocument[];
}

export interface AdminPayout {
  id: string;
  affiliateId: string;
  amountCents: number;
  currency: string;
  method: 'transfer' | 'manual' | 'mercadopago' | 'payoneer';
  reference: string | null;
  proofUrl: string | null;
  status: 'pending' | 'paid' | 'failed';
  notes: string | null;
  paidAt: string | null;
  createdAt: string;
}

export interface PayoutListResponse {
  items: AdminPayout[];
  total: number;
  page: number;
  limit: number;
  summary?: { totalPaidCents: number; totalPendingCents: number };
}

export interface AccountingBankInfo {
  /**
   * M1a: método explícito del bankInfo. Inferido por la migración
   * 0023 si el row viene de antes de este feature.
   */
  method?: 'transfer' | 'mercadopago' | 'payoneer' | 'manual';
  holder_name?: string;
  bank_name?: string;
  /**
   * M2a: incluye `'rut'` para Cuenta RUT / Vista (CL).
   */
  account_type?: 'checking' | 'savings' | 'rut' | string;
  account_number?: string;
  /**
   * M3a: RUT/ID del titular — siempre requerido, en cualquier método.
   */
  rut?: string;
  email_payoneer?: string;
  email_mercadopago?: string;
}

export interface AccountingPayoutRow {
  id: string;
  amountCents: number;
  currency: string;
  method: 'transfer' | 'manual' | 'mercadopago' | 'payoneer';
  reference: string | null;
  notes: string | null;
  status: 'pending' | 'paid' | 'failed';
  paidAt: string | null;
  createdAt: string;
  affiliateId: string;
  affiliateName: string;
  affiliateEmail: string;
  affiliateCountry: string | null;
  affiliateRut: string | null;
  bankInfo: AccountingBankInfo | null;
  hasBoleta: boolean;
  boletaDocumentId: string | null;
  boletaUploadedAt: string | null;
}

export interface AccountingPayoutListResponse {
  items: AccountingPayoutRow[];
  total: number;
  limit: number;
  offset: number;
}

export interface AdminOverviewStats {
  range: { from: string; to: string };
  affiliates: { total: number; active: number };
  inRange: {
    clicks: number;
    leads: number;
    revenueCents: number;
    commissionCents: number;
    conversions: number;
  };
  allTime: { conversions: number; commissionCents: number; pendingConversions: number };
  ratios: { conversionRate: number; earningsPerClick: number };
}

export interface AdminTopAffiliate {
  id: string;
  code: string;
  name: string;
  email: string;
  country: string | null;
  commissionCents: number;
  conversions: number;
  clicks: number;
  conversionRate: number;
}

export interface AuditEntry {
  id: string;
  actorId: string | null;
  actorType: 'admin' | 'affiliate' | 'system';
  action: string;
  targetId: string | null;
  targetType: string | null;
  metadata: string;
  ipHash: string | null;
  createdAt: string;
}

export interface SettingsMap {
  default_commission_pct: number;
  cookie_ttl_hours: number;
  payout_minimum_cents: number;
  auto_approve_commissions: boolean;
  cookie_domain: string;
  email_from: string;
  email_reply_to: string;
  lw_webhook_active: boolean;
  shopify_webhook_active: boolean;
}

export interface Material {
  id: string;
  name: string;
  type: 'banner' | 'template_email' | 'copy' | 'image' | 'pdf' | 'zip';
  thumbnailUrl: string | null;
  sizeBytes: number | null;
  mimeType: string | null;
  createdAt: string;
  /** D3b: curso al que pertenece el material. */
  courseId: string | null;
  courseName: string | null;
}

export interface AffiliateContract {
  id: string;
  affiliateId: string;
  name: string;
  mimeType: string | null;
  sizeBytes: number | null;
  uploadedAt: string;
}

export interface CommissionPromotion {
  id: string;
  affiliateId: string;
  courseId: string | null;
  course: { id: string; name: string; slug: string } | null;
  pct: string;
  startsAt: string;
  endsAt: string;
  note: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  status: 'active' | 'upcoming' | 'expired';
}

export interface AffiliateApplicationCreate {
  email: string;
  name: string;
  country: string;
  suggestedCode?: string;
  rut?: string;
  instagram?: string;
  youtube?: string;
  tiktok?: string;
  facebook?: string;
  linkedin?: string;
  audienceSize?: string;
  motivation?: string;
}

export interface AffiliateApplication {
  id: string;
  email: string;
  name: string;
  country: string;
  suggestedCode: string | null;
  suggestedCommissionPct: string | null;
  rut: string | null;
  instagram: string | null;
  youtube: string | null;
  tiktok: string | null;
  facebook: string | null;
  linkedin: string | null;
  audienceSize: string | null;
  motivation: string | null;
  status: 'pending' | 'approved' | 'rejected';
  rejectionReason: string | null;
  reviewedAt: string | null;
  createdAffiliateId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AffiliateApplicationStatus {
  id: string;
  status: 'pending' | 'approved' | 'rejected';
  createdAt: string;
  reviewedAt: string | null;
  rejectionReason: string | null;
}

// Re-export del shape que viene del backend (lo definimos en shared
// y lo importamos arriba, pero el patrón del archivo es declarar
// interfaces locales para el shape de respuestas).
export type { AdminUserView, CreateAdminUser, UpdateAdminRole };

export interface AffiliateApplicationListParams {
  page?: number;
  limit?: number;
  status?: 'pending' | 'approved' | 'rejected';
  search?: string;
}

export interface AffiliateLinks {
  code: string | null;
  links: { slug: string; name: string; url: string }[];
  pending?: boolean;
}

// ---- API endpoints ----

export const apiClient = {
  // Auth
  requestMagicLink: (email: string) =>
    api<{ ok: true }>('/api/v1/auth/magic-link', {
      method: 'POST',
      body: { email },
    }),
  logout: () => api<{ ok: true }>('/api/v1/auth/logout', { method: 'POST', withCredentials: true }),
  setPassword: (data: { newPassword: string; currentPassword?: string }) =>
    api<{ ok: true }>('/api/v1/auth/set-password', {
      method: 'POST',
      body: data,
      withCredentials: true,
    }),

  // Admin
  getOverview: (range: { from: string; to: string }) => {
    const qs = new URLSearchParams({ from: range.from, to: range.to });
    return api<AdminOverviewStats>(`/api/v1/admin/stats/overview?${qs}`, {
      withCredentials: true,
    });
  },
  getTopAffiliates: (range: { from: string; to: string }) => {
    const qs = new URLSearchParams({ from: range.from, to: range.to });
    return api<{ range: { from: string; to: string }; items: AdminTopAffiliate[] }>(
      `/api/v1/admin/stats/top-affiliates?${qs}`,
      { withCredentials: true },
    );
  },
  listAffiliates: (params: {
    page?: number;
    limit?: number;
    status?: string;
    search?: string;
    country?: string;
    fromDate?: string;
    toDate?: string;
  }) => {
    const qs = new URLSearchParams();
    if (params.page) qs.set('page', String(params.page));
    if (params.limit) qs.set('limit', String(params.limit));
    if (params.status) qs.set('status', params.status);
    if (params.search) qs.set('search', params.search);
    if (params.country) qs.set('country', params.country);
    if (params.fromDate) qs.set('fromDate', params.fromDate);
    if (params.toDate) qs.set('toDate', params.toDate);
    return api<AdminAffiliateListResponse>(`/api/v1/admin/affiliates?${qs}`, {
      withCredentials: true,
    });
  },
  getAffiliate: (id: string, range?: { from: string; to: string }) => {
    const qs = range ? `?from=${range.from}&to=${range.to}` : '';
    return api<AdminAffiliate & { stats: AffiliateStats }>(`/api/v1/admin/affiliates/${id}${qs}`, {
      withCredentials: true,
    });
  },
  getAffiliateProducts: (id: string, range: { from: string; to: string }) =>
    api<AffiliateProductsResponse>(
      `/api/v1/admin/affiliates/${id}/products?from=${range.from}&to=${range.to}`,
      {
        withCredentials: true,
      },
    ),
  createAffiliate: (data: AdminCreateAffiliateInput) =>
    api<AdminAffiliate>('/api/v1/admin/affiliates', {
      method: 'POST',
      body: data,
      withCredentials: true,
    }),
  updateAffiliate: (id: string, data: UpdateAffiliateInput) =>
    api<AdminAffiliate>(`/api/v1/admin/affiliates/${id}`, {
      method: 'PATCH',
      body: data,
      withCredentials: true,
    }),
  deleteAffiliate: (id: string) =>
    api<{ ok: true }>(`/api/v1/admin/affiliates/${id}`, {
      method: 'DELETE',
      withCredentials: true,
    }),
  getOnboarding: (id: string) =>
    api<AffiliateOnboarding>(`/api/v1/admin/affiliates/${id}/onboarding`, {
      withCredentials: true,
    }),
  setOnboardingStep: (id: string, step: OnboardingStepKey, done: boolean, courseId?: string) =>
    api<AffiliateOnboarding>(`/api/v1/admin/affiliates/${id}/onboarding`, {
      method: 'PATCH',
      body: { step, done, courseId },
      withCredentials: true,
    }),
  resendOnboardingLink: (id: string) =>
    api<{ ok: true }>(`/api/v1/admin/affiliates/${id}/onboarding-link`, {
      method: 'POST',
      withCredentials: true,
    }),

  // Courses (admin)
  listCourses: (params: { status?: 'draft' | 'released' | 'archived' } = {}) => {
    const qs = new URLSearchParams();
    if (params.status) qs.set('status', params.status);
    return api<{ items: Course[] }>(`/api/v1/admin/courses?${qs}`, { withCredentials: true });
  },
  getCourse: (id: string) => api<Course>(`/api/v1/admin/courses/${id}`, { withCredentials: true }),
  createCourse: (data: {
    slug: string;
    name: string;
    description?: string;
    defaultCommissionPct: number;
    defaultCookieTtlHours: number;
    /**
     * D1a: precio en centavos. D4b: opcional, default 0.
     */
    priceCents?: number;
    releasedAt?: string;
    status?: 'draft' | 'released' | 'archived';
  }) => api<Course>('/api/v1/admin/courses', { method: 'POST', body: data, withCredentials: true }),
  updateCourse: (
    id: string,
    data: Partial<{
      name: string;
      description: string | null;
      defaultCommissionPct: number;
      defaultCookieTtlHours: number;
      /** D5a: editable siempre. */
      priceCents?: number;
      releasedAt: string | null;
      status: 'draft' | 'released' | 'archived';
    }>,
  ) =>
    api<Course>(`/api/v1/admin/courses/${id}`, {
      method: 'PATCH',
      body: data,
      withCredentials: true,
    }),
  deleteCourse: (id: string) =>
    api(`/api/v1/admin/courses/${id}`, { method: 'DELETE', withCredentials: true }),

  // Assignments
  listCourseAssignments: (courseId: string) =>
    api<{ items: AffiliateCourse[] }>(`/api/v1/admin/courses/${courseId}/assignments`, {
      withCredentials: true,
    }),
  assignCourse: (
    courseId: string,
    data: {
      affiliateIds: string[];
      status?: 'pending' | 'active' | 'suspended';
    },
  ) =>
    api<{ items: AffiliateCourse[] }>(`/api/v1/admin/courses/${courseId}/assignments`, {
      method: 'POST',
      body: data,
      withCredentials: true,
    }),
  listCoursesLinksSummary: () =>
    api<{
      items: Array<{
        id: string;
        slug: string;
        name: string;
        description: string | null;
        status: 'draft' | 'released' | 'archived';
        createdAt: string;
        hasBancoInspiracional: boolean;
        bancoInspiracionalId: string | null;
        latestLink: {
          id: string;
          code: string;
          linkGeneratedAt: string | null;
          status: 'pending' | 'active' | 'suspended';
        } | null;
      }>;
    }>('/api/v1/admin/courses/links-summary', { withCredentials: true }),
  createCourseDemoLink: (courseId: string) =>
    api<AffiliateCourse>(`/api/v1/admin/courses/${courseId}/demo-link`, {
      method: 'POST',
      withCredentials: true,
    }),
  updateAffiliateCourse: (
    id: string,
    data: Partial<{
      status: 'pending' | 'active' | 'suspended';
      notes: string | null;
    }>,
  ) =>
    api<AffiliateCourse>(`/api/v1/admin/affiliate-courses/${id}`, {
      method: 'PATCH',
      body: data,
      withCredentials: true,
    }),
  deleteAffiliateCourse: (id: string) =>
    api(`/api/v1/admin/affiliate-courses/${id}`, {
      method: 'DELETE',
      withCredentials: true,
    }),
  listAffiliateCourses: (params: { affiliateId?: string; courseId?: string; status?: string }) => {
    const qs = new URLSearchParams();
    if (params.affiliateId) qs.set('affiliateId', params.affiliateId);
    if (params.courseId) qs.set('courseId', params.courseId);
    if (params.status) qs.set('status', params.status);
    return api<{ items: AffiliateCourse[] }>(`/api/v1/admin/affiliate-courses?${qs}`, {
      withCredentials: true,
    });
  },

  // Conversions (admin)
  listConversions: (
    params: {
      page?: number;
      limit?: number;
      status?: string;
      source?: string;
      affiliateId?: string;
      fromDate?: string;
      toDate?: string;
      country?: string;
      product?: string;
    } = {},
  ) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => v !== undefined && qs.set(k, String(v)));
    return api<ConversionListResponse>(`/api/v1/admin/conversions?${qs}`, {
      withCredentials: true,
    });
  },
  approveConversion: (id: string) =>
    api<AdminConversion>(`/api/v1/admin/conversions/${id}/approve`, {
      method: 'POST',
      withCredentials: true,
    }),
  rejectConversion: (id: string, reason: string) =>
    api<AdminConversion>(`/api/v1/admin/conversions/${id}/reject`, {
      method: 'POST',
      body: { reason },
      withCredentials: true,
    }),

  // Pagos (admin)
  listPayouts: (
    params: { page?: number; limit?: number; status?: string; affiliateId?: string } = {},
  ) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => v !== undefined && qs.set(k, String(v)));
    return api<PayoutListResponse>(`/api/v1/admin/payouts?${qs}`, { withCredentials: true });
  },
  createPayout: (data: {
    affiliateId: string;
    amountCents: number;
    method: 'transfer' | 'manual' | 'mercadopago' | 'payoneer';
    reference?: string;
    notes?: string;
  }) =>
    api<AdminPayout>('/api/v1/admin/payouts', {
      method: 'POST',
      body: data,
      withCredentials: true,
    }),
  markPayoutPaid: (
    id: string,
    data: {
      reference?: string;
      proofUrl?: string;
      notes?: string;
      amountCents?: number;
      method?: 'transfer' | 'manual' | 'mercadopago' | 'payoneer';
    },
  ) =>
    api<AdminPayout>(`/api/v1/admin/payouts/${id}/mark-paid`, {
      method: 'POST',
      body: data,
      withCredentials: true,
    }),

  // Accounting
  listAccountingPayouts: (
    params: {
      status?: 'pending' | 'paid' | 'failed';
      country?: string;
      month?: number;
      year?: number;
      limit?: number;
      offset?: number;
    } = {},
  ) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => v !== undefined && qs.set(k, String(v)));
    return api<AccountingPayoutListResponse>(`/api/v1/admin/accounting/payouts?${qs}`, {
      withCredentials: true,
    });
  },
  accountingExportUrl: (
    params: {
      status?: 'pending' | 'paid' | 'failed';
      country?: string;
      month?: number;
      year?: number;
    } = {},
  ) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => v !== undefined && qs.set(k, String(v)));
    return `/api/v1/admin/accounting/payouts/export.csv?${qs}`;
  },
  markAccountingPayoutPaid: (
    id: string,
    data: {
      reference?: string;
      proofUrl?: string;
      notes?: string;
      amountCents?: number;
      method?: 'transfer' | 'manual' | 'mercadopago' | 'payoneer';
    },
  ) =>
    api<AdminPayout>(`/api/v1/admin/accounting/payouts/${id}/mark-paid`, {
      method: 'POST',
      body: data,
      withCredentials: true,
    }),
  accountingDownloadBoletaUrl: (id: string) => `/api/v1/admin/documents/${id}/download`,

  // Settings + audit
  getSettings: () => api<SettingsMap>('/api/v1/admin/settings', { withCredentials: true }),
  updateSetting: (key: string, value: string | number | boolean) =>
    api(`/api/v1/admin/settings`, { method: 'PATCH', body: { key, value }, withCredentials: true }),
  listAudit: (params: { page?: number; limit?: number } = {}) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => v !== undefined && qs.set(k, String(v)));
    return api<{ items: AuditEntry[]; total: number; page: number; limit: number }>(
      `/api/v1/admin/audit?${qs}`,
      { withCredentials: true },
    );
  },

  // Admin user management (sólo owner/manager)
  listAdminUsers: () =>
    api<{ items: AdminUserView[] }>('/api/v1/admin/users', { withCredentials: true }),
  inviteAdminUser: (data: CreateAdminUser) =>
    api<AdminUserView>('/api/v1/admin/users', {
      method: 'POST',
      body: data,
      withCredentials: true,
    }),
  updateAdminUserRole: (id: string, role: UpdateAdminRole['role']) =>
    api<AdminUserView>(`/api/v1/admin/users/${id}`, {
      method: 'PATCH',
      body: { role },
      withCredentials: true,
    }),
  deleteAdminUser: (id: string) =>
    api<{ ok: true }>(`/api/v1/admin/users/${id}`, {
      method: 'DELETE',
      withCredentials: true,
    }),

  // Affiliate self
  getAffiliateMe: () =>
    api<
      AdminAffiliate & {
        code: string;
        bankInfo: unknown;
        metadata: string;
        courseOnboarding: CourseOnboardingByCourseMap;
        /** `true` si el creator ya tiene `passwordHash` seteado.
         *  Lo usa el form de Seguridad para decidir si pedir la
         *  contraseña actual antes de cambiarla. */
        hasPassword: boolean;
      }
    >('/api/v1/affiliate/me', { withCredentials: true }),
  updateAffiliateMe: (data: {
    name?: string;
    country?: string;
    rut?: string;
    phone?: string;
    instagram?: string;
    youtube?: string;
    tiktok?: string;
    facebook?: string;
    linkedin?: string;
    audienceSize?: string;
    bankInfo?: unknown;
  }) => api('/api/v1/affiliate/me', { method: 'PATCH', body: data, withCredentials: true }),
  getMyStats: () => api<AffiliateStats>('/api/v1/affiliate/me/stats', { withCredentials: true }),
  getMyLinks: () => api<AffiliateLinks>('/api/v1/affiliate/me/links', { withCredentials: true }),
  getMyConversions: (params: { page?: number; limit?: number; status?: string } = {}) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => v !== undefined && qs.set(k, String(v)));
    return api<ConversionListResponse>(`/api/v1/affiliate/conversions?${qs}`, {
      withCredentials: true,
    });
  },
  getMyPayouts: (params: { page?: number; limit?: number } = {}) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => v !== undefined && qs.set(k, String(v)));
    return api<PayoutListResponse>(`/api/v1/affiliate/payouts?${qs}`, { withCredentials: true });
  },

  // Signed contract — read-only for the creator (download only).
  // The admin (Educrea) is the one who uploads and replaces the file;
  // see adminUploadAffiliateContract / adminDeleteAffiliateContract
  // below. 1:1 with the creator, replace in place.
  getMyContract: () =>
    api<{ item: AffiliateContract | null }>('/api/v1/affiliate/me/contract', {
      withCredentials: true,
    }),
  myContractDownloadUrl: () => '/api/v1/affiliate/me/contract/download',

  // Admin: upload, replace, and delete the signed contract of any
  // creator. The creator only sees (downloads) the result.
  getAffiliateContract: (affiliateId: string) =>
    api<{ item: AffiliateContract | null }>(`/api/v1/admin/affiliates/${affiliateId}/contract`, {
      withCredentials: true,
    }),
  affiliateContractDownloadUrl: (affiliateId: string) =>
    `/api/v1/admin/affiliates/${affiliateId}/contract/download`,
  adminUploadAffiliateContract: (affiliateId: string, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return api<{ item: AffiliateContract }>(`/api/v1/admin/affiliates/${affiliateId}/contract`, {
      method: 'POST',
      body: fd,
      withCredentials: true,
    });
  },
  adminDeleteAffiliateContract: (affiliateId: string) =>
    api<{ ok: true }>(`/api/v1/admin/affiliates/${affiliateId}/contract`, {
      method: 'DELETE',
      withCredentials: true,
    }),

  // Materials
  getMaterials: () =>
    api<{ items: Material[] }>('/api/v1/affiliate/me/materials', { withCredentials: true }),
  materialDownloadUrl: (id: string) => `/api/v1/affiliate/me/materials/${id}/download`,
  getMyCourseMaterial: (courseId: string) =>
    api<{ item: Material | null }>(`/api/v1/affiliate/me/courses/${courseId}/material`, {
      withCredentials: true,
    }),
  getAdminMaterials: () =>
    api<{ items: Material[] }>('/api/v1/admin/materials/admin?page=1&limit=100', {
      withCredentials: true,
    }),
  getAdminMaterialByCourse: (courseId: string) =>
    api<{ item: Material | null }>(
      `/api/v1/admin/materials/admin/by-course?courseId=${encodeURIComponent(courseId)}`,
      { withCredentials: true },
    ),
  adminUploadMaterial: (input: { courseId: string; file: File; name?: string }) => {
    const fd = new FormData();
    fd.append('courseId', input.courseId);
    if (input.name) fd.append('name', input.name);
    fd.append('file', input.file);
    return api<Material>('/api/v1/admin/materials', {
      method: 'POST',
      body: fd,
      withCredentials: true,
    });
  },
  adminDeleteMaterial: (id: string) =>
    api<{ ok: true }>(`/api/v1/admin/materials/${id}`, { method: 'DELETE', withCredentials: true }),
  materialAdminDownloadUrl: (id: string) => `/api/v1/admin/materials/${id}/download`,

  // Courses (own)
  getMyCourses: () =>
    api<{ items: AffiliateCourse[] }>('/api/v1/affiliate/me/courses', { withCredentials: true }),

  // Documents (affiliate)
  uploadDocument: (input: { periodMonth: number; periodYear: number; file: File }) => {
    const fd = new FormData();
    fd.append('periodMonth', String(input.periodMonth));
    fd.append('periodYear', String(input.periodYear));
    fd.append('file', input.file);
    return api<AffiliateDocument>('/api/v1/affiliate/documents', {
      method: 'POST',
      body: fd,
      withCredentials: true,
    });
  },
  getMyDocuments: () =>
    api<AffiliateDocumentsResponse>('/api/v1/affiliate/documents', { withCredentials: true }),
  downloadDocumentUrl: (id: string) => `/api/v1/affiliate/documents/${id}/download`,
  deleteMyDocument: (id: string) =>
    api<{ ok: true }>(`/api/v1/affiliate/documents/${id}`, {
      method: 'DELETE',
      withCredentials: true,
    }),

  // Documents (admin)
  listAllDocuments: (filters: { affiliateId?: string } = {}) => {
    const qs = new URLSearchParams();
    if (filters.affiliateId) qs.set('affiliateId', filters.affiliateId);
    return api<AffiliateDocumentsResponse>(`/api/v1/admin/documents?${qs}`, {
      withCredentials: true,
    });
  },
  downloadDocumentAdminUrl: (id: string) => `/api/v1/admin/documents/${id}/download`,

  // Promotions
  listPromotions: (affiliateId: string) =>
    api<{ items: CommissionPromotion[] }>(`/api/v1/admin/affiliates/${affiliateId}/promotions`, {
      withCredentials: true,
    }),
  createPromotion: (
    affiliateId: string,
    data: { pct: number; startsAt: string; endsAt: string; courseId?: string; note?: string },
  ) =>
    api<CommissionPromotion>(`/api/v1/admin/affiliates/${affiliateId}/promotions`, {
      method: 'POST',
      body: data,
      withCredentials: true,
    }),
  deletePromotion: (affiliateId: string, promoId: string) =>
    api(`/api/v1/admin/affiliates/${affiliateId}/promotions/${promoId}`, {
      method: 'DELETE',
      withCredentials: true,
    }),

  // Applications (public + admin)
  submitApplication: (data: AffiliateApplicationCreate) =>
    api<{ id: string }>('/api/v1/applications', {
      method: 'POST',
      body: data,
    }),
  getApplicationStatus: (id: string) =>
    api<AffiliateApplicationStatus>(`/api/v1/applications/${id}/status`),
  listApplications: (params: AffiliateApplicationListParams = {}) => {
    const qs = new URLSearchParams();
    if (params.page) qs.set('page', String(params.page));
    if (params.limit) qs.set('limit', String(params.limit));
    if (params.status) qs.set('status', params.status);
    if (params.search) qs.set('search', params.search);
    return api<{ items: AffiliateApplication[]; total: number; page: number; limit: number }>(
      `/api/v1/admin/applications?${qs}`,
      { withCredentials: true },
    );
  },
  getApplication: (id: string) =>
    api<AffiliateApplication>(`/api/v1/admin/applications/${id}`, { withCredentials: true }),
  approveApplication: (
    id: string,
    data: { name?: string; code?: string; commissionPct?: number },
  ) =>
    api<{ affiliateId: string; magicLinkUrl: string }>(`/api/v1/admin/applications/${id}/approve`, {
      method: 'POST',
      body: data,
      withCredentials: true,
    }),
  rejectApplication: (id: string, reason: string) =>
    api(`/api/v1/admin/applications/${id}/reject`, {
      method: 'POST',
      body: { reason },
      withCredentials: true,
    }),
};
